import { PageCode, useLogger } from "@amzn/xb-hooks";
import { createContext, PropsWithChildren, useContext, useState } from "react";
import { LiveComponentSourceProps, LiveInterceptorFactory } from "../live-component";

export const StaticPageInterceptorContext = createContext<(pageCode?: PageCode | null) => () => void>(() => () => {});

export type StaticPage = React.ComponentType<PropsWithChildren<{ pageCode: PageCode }>>;
export type StaticPages = Record<PageCode, StaticPage>;
export const StaticPagesContext = createContext<StaticPages>({
  // These should be implemented by the XB application
  E400: () => <>400 Page</>,
  E403: () => <>403 Page</>,
  E404: () => <>404 Page</>,
  E408: () => <>408 Page</>,
  E500: () => <>500 Page</>,
  Maintenance: () => <>Maintenance Page</>,
});

/**
 *  Short-circuits rendering the PageComponent and displays a static
 *  page when requested.
 */
export const staticPageInterceptorFactory: LiveInterceptorFactory = (_, Response) => {
  const StaticPageInterceptor = (props: LiveComponentSourceProps) => {
    const { debug } = useLogger();
    const [pageCode, setPageCode] = useState<PageCode | null>(null);
    const pages = useContext(StaticPagesContext);
    const StaticPage = pageCode ? pages[pageCode] : () => <></>;

    const displayPageCallback = (code?: PageCode | null) => {
      debug("displayPageCallback", code);
      if (!code) {
        return () => setPageCode(null);
      }
      return setPageCode(code), () => setPageCode(null);
    };

    debug("page code received at static page interceptor", pageCode);

    return (
      <>
        <StaticPageInterceptorContext.Provider value={displayPageCallback}>
          {pageCode ? <StaticPage pageCode={pageCode} /> : <Response {...props} />}
        </StaticPageInterceptorContext.Provider>
      </>
    );
  };
  return StaticPageInterceptor;
};
